/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Button, Empty, Modal, Typography, message } from 'antd';
import {
  CommentOutlined,
  DownOutlined,
  UpOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { CommentForm } from './CommentForm';
import { CommentItem } from './CommentItem';
import type {
  ReviewServiceFunctions,
  CommentFormData,
  ReviewComment,
} from './types';

type CommentSectionProps = {
  reviewId: number;
  entityId: number;
  contextIds: Record<string, number | string>;
  reviewService: ReviewServiceFunctions;
  comments: ReviewComment[];
  onCommentsChange?: () => void;
  showComments?: boolean;
  isReadOnly?: boolean;
};

export const CommentSection: React.FC<CommentSectionProps> = ({
  reviewId,
  entityId,
  contextIds,
  reviewService,
  comments,
  onCommentsChange,
  showComments: initialShowComments = false,
  isReadOnly = false,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const [showComments, setShowComments] = useState(initialShowComments);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [replyingToCommentId, setReplyingToCommentId] = useState<number | null>(
    null
  );
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);

  const [doCreateComment, { isLoading: isCreatingComment }] = useMutation(
    (body: CommentFormData) => {
      if (!reviewService.createComment) {
        return Promise.reject(new Error('createComment not available'));
      }
      return reviewService.createComment({ entityId, reviewId, body });
    },
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'comment-created-success' }));
        setIsAddingComment(false);
        if (onCommentsChange) {
          onCommentsChange();
        }
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const [doCreateAnswer, { isLoading: isCreatingAnswer }] = useMutation(
    ({ commentId, body }: { commentId: number; body: CommentFormData }) => {
      if (!reviewService.createAnswer) {
        return Promise.reject(new Error('createAnswer not available'));
      }
      return reviewService.createAnswer({
        entityId,
        reviewId,
        commentId,
        body,
      });
    },
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'comment-answer-created-success' })
        );
        setReplyingToCommentId(null);
        if (onCommentsChange) {
          onCommentsChange();
        }
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const [doUpdateComment, { isLoading: isUpdatingComment }] = useMutation(
    ({ commentId, body }: { commentId: number; body: CommentFormData }) => {
      if (!reviewService.updateComment) {
        return Promise.reject(new Error('updateComment not available'));
      }
      return reviewService.updateComment({
        entityId,
        reviewId,
        commentId,
        body,
      });
    },
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'comment-updated-success' }));
        setEditingCommentId(null);
        if (onCommentsChange) {
          onCommentsChange();
        }
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const [doDeleteComment] = useMutation(
    (commentId: number) => {
      if (!reviewService.deleteComment) {
        return Promise.reject(new Error('deleteComment not available'));
      }
      return reviewService.deleteComment({ entityId, reviewId, commentId });
    },
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'comment-deleted-success' }));
        if (onCommentsChange) {
          onCommentsChange();
        }
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const handleDeleteComment = (commentId: number) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'comment-delete-confirm-title' }),
      content: intl.formatMessage({ id: 'comment-delete-confirm-content' }),
      okText: intl.formatMessage({ id: 'delete' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      okType: 'danger',
      onOk: () => {
        doDeleteComment(commentId);
      },
    });
  };

  const findCommentById = (commentId: number): ReviewComment | null => {
    for (const comment of comments) {
      if (comment.id === commentId) return comment;
      if (comment.answers) {
        const answer = comment.answers.find((a) => a.id === commentId);
        if (answer) return answer;
      }
    }
    return null;
  };

  const totalComments = comments.length;
  const hasComments = totalComments > 0;
  const canAddComments = !!reviewService.createComment && !isReadOnly;

  // Don't render if no comments and user can't add comments
  if (!hasComments && !canAddComments) {
    return null;
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Button
          type="text"
          icon={
            showComments ? <UpOutlined rev={''} /> : <DownOutlined rev={''} />
          }
          onClick={() => setShowComments(!showComments)}
          css={styles.toggleButton}>
          <CommentOutlined rev={''} />
          {intl.formatMessage(
            { id: 'comment-toggle-comments' },
            { count: totalComments }
          )}
        </Button>
      </div>

      {showComments && (
        <div css={styles.content}>
          {canAddComments &&
            !isAddingComment &&
            !replyingToCommentId &&
            !editingCommentId && (
              <Button
                type="dashed"
                icon={<PlusOutlined rev={''} />}
                onClick={() => setIsAddingComment(true)}
                css={styles.addButton}>
                {intl.formatMessage({ id: 'comment-add-comment-button' })}
              </Button>
            )}

          {isAddingComment && (
            <CommentForm
              onSubmit={(data) => doCreateComment(data)}
              onCancel={() => setIsAddingComment(false)}
              isSubmitting={isCreatingComment}
            />
          )}

          {!hasComments && !isAddingComment ? (
            <Empty
              description={
                <Typography.Text type="secondary">
                  {intl.formatMessage({ id: 'comment-no-comments-yet' })}
                </Typography.Text>
              }
              css={styles.emptyState}
            />
          ) : (
            <div css={styles.commentsList}>
              {comments.map((comment) => {
                const isReplying = replyingToCommentId === comment.id;

                return (
                  <div key={comment.id}>
                    <CommentItem
                      comment={comment}
                      isCurrentUser={false}
                      onEdit={
                        comment.can_edit && !isReadOnly
                          ? () => setEditingCommentId(comment.id)
                          : undefined
                      }
                      onDelete={
                        comment.can_delete && !isReadOnly
                          ? () => handleDeleteComment(comment.id)
                          : undefined
                      }
                      onReply={
                        reviewService.createAnswer && !isReadOnly
                          ? () => setReplyingToCommentId(comment.id)
                          : undefined
                      }
                      onEditAny={(id) => setEditingCommentId(id)}
                      onDeleteAny={(id) => handleDeleteComment(id)}
                      editingCommentId={editingCommentId}
                      onCancelEdit={() => setEditingCommentId(null)}
                      onSubmitEdit={(commentId, data) =>
                        doUpdateComment({ commentId, body: data })
                      }
                      isSubmittingEdit={isUpdatingComment}
                      isReadOnly={isReadOnly}
                    />

                    {isReplying && (
                      <div css={styles.replyForm}>
                        <CommentForm
                          onSubmit={(data) =>
                            doCreateAnswer({
                              commentId: comment.id,
                              body: data,
                            })
                          }
                          onCancel={() => setReplyingToCommentId(null)}
                          isSubmitting={isCreatingAnswer}
                          isAnswer
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  container: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid #f0f0f0',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },

  toggleButton: {
    fontSize: 13,
    fontWeight: 500,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },

  content: {
    marginTop: 12,
  },

  addButton: {
    width: '100%',
    marginBottom: 12,
  },

  commentsList: {
    display: 'flex',
    flexDirection: 'column',
  },

  replyForm: {
    marginLeft: 24,
    marginTop: 8,
    marginBottom: 12,
  },

  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  emptyState: {
    padding: '20px',
  },
});
